<template>
  <l-map ref="myMap" :options="{ zoomControl: false, competitionId: competition.id }"
    @ready="(map = $event), readGpxFiles(), $emit('ready', $event)" :zoom="zoom" class="map">
    <l-tile-layer :url="url" />
    <l-control-zoom position="bottomright" />
    <l-control position="bottomleft">
      <div class="d-flex">
        <div class="mode">
          <!-- <div class="mode__switch">
            <div class="mode__switch-btn" :class="{
              'mode__switch-btn--active': isLiveMode,
            }"></div>
          </div> -->
          <div class="mode__text">
            <span :class="{ 'mode__text--red mar10': isLiveMode }">LIVE</span>
            <i data-v-0f289c33="" class="fas fa-video" style="color: rgb(255, 23, 68); margin-right: 5px;"></i>
            <template v-if="isLiveMode">{{
              isPastCurrentClassification()
                ? getDurationPastCompetition()
                : liveTime
            }}</template>
          </div>
        </div>
      </div>
    </l-control>
    <!-- Slot content. -->
    <slot />
  </l-map>
</template>

<script>
import L from 'leaflet'
import { LMap, LTileLayer, LControlZoom, LControl } from 'vue2-leaflet'
import { Icon } from 'leaflet'
import 'leaflet-gpx'
import { mapGetters, mapMutations } from 'vuex'
import {
  SET_SELECTED_CLASSIFICATION_ID,
  SET_TRACK_BOUNDS,
  SET_MODE,
} from '@/store/mutations.type'
import { eventBus } from '@/main'
import moment from 'moment'
import {
  setDistanceMarkers,
  removeDistanceMarkers,
} from '@/utils/distanceMarkers'
export default {
  components: {
    LMap,
    LTileLayer,
    LControlZoom,
    LControl,
  },
  props: {
    classifications: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      zoom: 13,
      tracks: {},
      highlightedTrackColor: '#37474f',
      disabledTrackColor: '#78909c',
      map: null,
      liveTime: '00:00:00',
    }
  },
  computed: {
    ...mapGetters([
      'gpxFiles',
      'selectedClassificationId',
      'isLiveMode',
      'competitions',
      'devices',
      'isShowDistanceMarkers',
      'amountDistanceMarkers',
      'isRevertDistanceMarkers',
      'isRevertDistanceMarkers',
    ]),

    classificationsIds() {
      const classificationsIds = []
      for (const classification of this.classifications) {
        classificationsIds.push(classification.id)
      }
      return classificationsIds
    },
    competition() {
      return this.competitions[this.classifications[0].competition]
    },
    currentClassification() {
      return this.classifications.find(
        (cls) => cls.id === this.selectedClassificationId
      )
    },
  },
  created() {
    this.deleteDefaultIcons()
    eventBus.$on('selectedClassification', () => {
      for (const trackId in this.tracks) {
        if (
          this.tracks[trackId].classification ===
          this.selectedClassificationId
        ) {
          this.centerOnTrack(this.tracks[trackId])
        }
      }
    })
  },
  methods: {
    ...mapMutations([
      SET_SELECTED_CLASSIFICATION_ID,
      SET_TRACK_BOUNDS,
      SET_MODE,
    ]),
    isPastCurrentClassification() {
      if (this.currentClassification.endTime) {
        return true
      } else {
        return false
      }
    },
    getDurationPastCompetition() {
      const endTime = this.currentClassification.endTime
      const startTime =
        this.currentClassification.startTime ??
        this.competition.startedTime
      let diff = endTime.diff(startTime)
      let duration = moment.duration(diff)
      let days = duration.get('d')
      let time = (time = moment
        .utc(duration.asMilliseconds())
        .format('HH:mm:ss'))
      if (days > 0) {
        // [hh, mm, ss]
        let timeElements = time.split(':')
        timeElements[0] = Number.parseInt(timeElements[0]) + days * 24
        time = timeElements.join(':')
      }
      return time
    },
    getLiveTime() {
      if (this.isLiveMode) {
        let diff, startTime, currentTime, duration, time
        startTime = this.currentClassification.startTime
          ? this.currentClassification.startTime
          : this.competition.startedTime

        currentTime = moment()
        diff = currentTime.diff(startTime)
        if (diff < 0) {
          return (this.liveTime = '00:00:00')
        }
        duration = moment.duration(diff)
        let d = duration.get('d')
        time = moment.utc(duration.asMilliseconds()).format('HH:mm:ss')
        if (d > 0) {
          // [hh, mm, ss]
          let timeElements = time.split(':')
          timeElements[0] = Number.parseInt(timeElements[0]) + d * 24
          time = timeElements.join(':')
          return (this.liveTime = time)
        }
        return (this.liveTime = time)
      }
      return 0
    },
    deleteDefaultIcons() {
      delete Icon.Default.prototype._getIconUrl
    },

    readGpxFiles() {
      this.tracks = {}
      for (const id of this.classificationsIds) {
        this.tracks[id] = new L.GPX(this.gpxFiles[id].url, {
          async: true,
          gpx_options: {
            parseElements: ['track', 'route'],
          },
          polyline_options: {
            color: this.disabledTrackColor,
            opacity: 1,
            weight: 5,
            lineCap: 'round',
          },
          marker_options: {
            startIconUrl: null,
            endIconUrl: null,
            shadowUrl: null,
          },
        })
          .on('loaded', (e) => {
            // TrackBound's index is classification id.
            let trackbound = e.target.getBounds().pad(0.3)
            trackbound.id = id
            e.target.classification = id
            this.SET_TRACK_BOUNDS(trackbound)
            e.target.bringToBack()
            if (this.selectedClassificationId === id) {
              this.centerOnTrack(e.target)
              this.setDisabledTracks()
              this.highlightTrack(e.target)
            }
          })
          .on('click', (e) => this.trackClickHandler(e.target))
          .on('mouseover', (e) => {
            this.setDisabledTracks()
            this.highlightTrack(e.target)
          })
          .on('mouseout', () => {
            const selectedTrack = this.findSelectedTrack()
            this.setDisabledTracks()
            this.highlightTrack(selectedTrack)
          })
          .addTo(this.map)
      }
      this.$emit('gpx:ready')
    },
    trackClickHandler(track) {
      // Change selected classification.
      const [clickedClassification] = this.classifications.filter(
        (classification) => classification.id === track.classification
      )
      this.SET_SELECTED_CLASSIFICATION_ID(clickedClassification.id)
    },
    centerOnTrack(track) {
      this.map.fitBounds(track.getBounds())
    },
    setDisabledTracks() {
      for (const trackId in this.tracks) {
        if (
          Object.prototype.hasOwnProperty.call(this.tracks, trackId)
        ) {
          this.tracks[trackId].bringToBack()
          this.tracks[trackId].setStyle({
            color: this.disabledTrackColor,
          })
        }
      }
    },
    highlightTrack(track) {
      if (track !== undefined) {
        track.bringToFront()
        track.setStyle({ color: this.highlightedTrackColor })
      }
    },
    clearGpxLayers() {
      this.map.eachLayer((layer) => {
        // eslint-disable-next-line no-underscore-dangle
        if (layer._gpx) {
          this.map.removeLayer(layer)
        }
      })
    },
    findSelectedTrack() {
      for (const trackId in this.tracks) {
        if (
          Number.parseInt(trackId, 10) ===
          this.selectedClassificationId
        ) {
          return this.tracks[trackId]
        }
      }
      return undefined
    },
    switchMode() {
      this.SET_MODE(!this.isLiveMode)
    },
    showDistanceMarkers() {
      if (this.map) {
        removeDistanceMarkers(this.map)
        let currentLayer
        this.map.eachLayer((layer) => {
          if (layer.classification === this.selectedClassificationId) {
            currentLayer = layer
          }
        })
        const latLngs = currentLayer.getLayers()[0].getLatLngs()
        const maxDistance = currentLayer.get_distance()
        const offset = Math.floor(maxDistance / this.amountDistanceMarkers)
        setDistanceMarkers(
          latLngs,
          this.map,
          offset,
          maxDistance,
          this.isRevertDistanceMarkers
        )
      } else {
        setTimeout(() => this.showDistanceMarkers(), 1000)
      }
    },
  },

  watch: {
    selectedClassificationId() {
      if (this.isShowDistanceMarkers) { this.showDistanceMarkers() }
      this.setDisabledTracks()
      this.isPastCurrentClassification()
      for (const trackId in this.tracks) {
        if (
          this.tracks[trackId].classification ===
          this.selectedClassificationId
        ) {
          this.highlightTrack(this.tracks[trackId])
          // this.centerOnTrack(this.tracks[trackId])
        }
      }
    },
    isLiveMode: {
      immediate: true,
      handler() {
        if (this.isLiveMode && !this.isPastCurrentClassification()) {
          window.timeInterval = setInterval(this.getLiveTime, 500)
        } else {
          clearInterval(window.timeInterval)
        }
      },
    },
    isShowDistanceMarkers: {
      immediate: true,
      handler() {
        if (!this.isShowDistanceMarkers) {
          removeDistanceMarkers(this.map)
        } else {
          this.showDistanceMarkers()
        }
      }
    },
    amountDistanceMarkers() {
      this.showDistanceMarkers()
    },
    isRevertDistanceMarkers() {
      this.showDistanceMarkers()
    },
  },
}
</script>
<style lang="scss" scoped>
.map {
  border-radius: 4px;
}

.mode {
  border-radius: 10px;
  box-shadow: 0 8px 12px 0 rgba(0, 0, 0, 0.1), 0 0 2px 0 rgba(55, 71, 79, 0.2);
  background-color: $white;
  display: flex;
  padding: 10px 16px;
  line-height: 1.43;

  &__switch {
    height: 100%;

    &-btn {
      width: 40px;
      height: 24px;
      background-color: $pale-grey;
      border-radius: 12px;
      position: relative;
      cursor: pointer;
      transition: all 0.3s;

      &::after {
        transition: all 0.3s;
        content: '';
        position: absolute;
        top: 2px;
        left: 2px;
        width: 20px;
        height: 20px;
        border-radius: 12px;
        display: inline-block;
        background-color: $white;
        box-shadow: 0 8px 12px 0 rgba(0, 0, 0, 0.1),
          0 0 2px 0 rgba(55, 71, 79, 0.2);
      }

      &--active {
        background-color: $red-orange;
        opacity: 1;

        &::after {
          left: 18px;
        }
      }
    }
  }

  &__text {
    font-size: 18px;
    transition: all 0.4s;

    &--red {
      color: $pinky-red;
    }
  }
}

@keyframes lds-roller {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.loading {
  animation: lds-roller 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}
</style>
